import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useId, useRef } from 'react';
import Screen from './Screen';
import { registerKeyHandler, unregisterKeyHandler } from './GlobalKeyHandler';
import './Popup.css';
import { globalFocus } from '../helpers/dom';
/**
 * Popups are, depending on screen size, either modal dialogs or floating windows that
 * can be closed by clicking outside of them or by pressing the escape key.
 * On mobile, they show a close button in the left right corner.
 */
const Popup = ({ show, title, onClose, closable = true, children, minWidth, minHeight, backdrop = true, position = 'center', menuAnchor, closeButton = 'closeOnMobile', closeFocus, className, topRightIcon, flowForm }) => {
    const ref = React.createRef();
    const id = 'popup-' + useId();
    // Element focused before the popup opened, restored on close so focus returns to
    // whatever triggered it (e.g. a context-menu button). Explicit closeFocus wins.
    const openerRef = useRef(null);
    const prevShowRef = useRef(false);
    function closed() {
        var _a;
        onClose();
        globalFocus((_a = closeFocus !== null && closeFocus !== void 0 ? closeFocus : openerRef.current) !== null && _a !== void 0 ? _a : undefined);
    }
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f;
        if (show) {
            if (!prevShowRef.current)
                openerRef.current = document.activeElement;
            prevShowRef.current = true;
            registerKeyHandler(id, {
                'Escape': () => {
                    if (closable)
                        closed();
                    return true;
                },
                // FlowForm popups let Enter activate the focused control, and use ArrowDown
                // to step into the dialog; plain popups keep the legacy Enter-closes shortcut.
                ...(flowForm ? {
                    'ArrowDown': () => {
                        var _a, _b;
                        // Only step into the dialog when focus is still outside it; otherwise let
                        // the focused control's own handler manage arrow navigation.
                        const active = document.activeElement;
                        if (active && ((_a = ref.current) === null || _a === void 0 ? void 0 : _a.contains(active)))
                            return false;
                        const first = (_b = ref.current) === null || _b === void 0 ? void 0 : _b.querySelector('.flowFocussable:not(.sidewayFocus):not(.close)');
                        first === null || first === void 0 ? void 0 : first.focus();
                        return true;
                    },
                } : {
                    'Enter': () => {
                        if (closable)
                            closed();
                        return true;
                    },
                }),
            }, true, 1002);
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.showModal();
            // Inherit the current keyboard/mouse focus mode so a control focused on open
            // shows (or hides) its focus ring to match how the popup was triggered.
            if (flowForm && ref.current) {
                const keyboardMode = !!document.querySelector('.flowForm.visibleFocus, .flowFormModal.visibleFocus');
                ref.current.classList.add(keyboardMode ? 'visibleFocus' : 'hiddenFocus');
            }
            if (ref.current && position == 'menu-right' && menuAnchor) {
                if (window.matchMedia("(max-width: 600px)").matches)
                    ref.current.style.marginTop = '';
                else {
                    const b = (_b = (typeof menuAnchor == 'string' ? document.querySelector(menuAnchor) : menuAnchor)) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
                    const y = ((_c = b === null || b === void 0 ? void 0 : b.top) !== null && _c !== void 0 ? _c : 0) + ((_d = b === null || b === void 0 ? void 0 : b.height) !== null && _d !== void 0 ? _d : 0);
                    ref.current.style.marginTop = y + 'px';
                }
            }
        }
        else {
            // Cover close paths that call onClose directly (e.g. a content button) and
            // unmount the dialog before its own close handler can restore focus.
            if (prevShowRef.current)
                globalFocus((_e = closeFocus !== null && closeFocus !== void 0 ? closeFocus : openerRef.current) !== null && _e !== void 0 ? _e : undefined);
            prevShowRef.current = false;
            (_f = ref.current) === null || _f === void 0 ? void 0 : _f.close();
            unregisterKeyHandler(id);
        }
        return () => {
            unregisterKeyHandler(id);
        };
    }, [show, closable, onClose, ref.current]);
    return show ?
        _jsx("dialog", { ref: ref, className: `popup ${flowForm ? 'flowFormModal ' : ''}${className !== null && className !== void 0 ? className : ''} ${position}${backdrop ? ' backdrop' : ' no-backdrop'}`, style: { minWidth, minHeight }, onClose: closed, onClick: closed, children: _jsx(Screen, { title: title, onClose: closed, closeButton: closeButton, topRightIcon: topRightIcon, children: children }) })
        : _jsx(_Fragment, {});
};
export default Popup;
